var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { PlayCircleOutline as ActivateIcon, PauseCircleOutline as DeactivateIcon, DeleteOutline as DeleteIcon, } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography, } from "@mui/material";
import { format } from "date-fns";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useRemoveTeamFromLeague, useRetrieveLeague, useRetrieveAllLeagueTeams, useUpdateTeamMembershipStatus, } from "../api/LeagueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { NotFound } from "../nav/NotFound";
import { TeamAvatar } from "../teams/TeamAvatar";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export var ManageTeams = function () {
    var id = useNumericParams().id;
    var _a = __read(useState(null), 2), teamToRemove = _a[0], setTeamToRemove = _a[1];
    var _b = __read(useState(null), 2), teamToDeactivate = _b[0], setTeamToDeactivate = _b[1];
    var _c = __read(useState(null), 2), actionLoading = _c[0], setActionLoading = _c[1];
    var _d = __read(useState(""), 2), successMessage = _d[0], setSuccessMessage = _d[1];
    var _e = __read(useState(""), 2), errorMessage = _e[0], setErrorMessage = _e[1];
    var _f = useRetrieveLeague({ id: id }), league = _f.data, isLoading = _f.isLoading, error = _f.error;
    var _g = useRetrieveUserLeaguePermissions({
        leagueId: id,
    }), permissions = _g.data, isPermsLoading = _g.isLoading, permsError = _g.error;
    var _h = permissions || {}, _j = _h.canView, canView = _j === void 0 ? false : _j, _k = _h.canModify, canModify = _k === void 0 ? false : _k;
    var _l = useRetrieveAllLeagueTeams({ leagueId: id, enabled: canView }), teams = _l.data, isTeamsLoading = _l.isLoading, teamsError = _l.error;
    var updateStatusMutation = useUpdateTeamMembershipStatus({
        leagueId: id,
        onMutate: function (_a) {
            var teamId = _a.teamId;
            return setActionLoading(teamId);
        },
        onSuccess: function (data) {
            setActionLoading(null);
            // Get team name from the response data
            var teamName = data.data.team.name;
            var isActive = data.data.isActive;
            setSuccessMessage("".concat(teamName, " has been ").concat(isActive ? "activated" : "deactivated", " successfully!"));
        },
        onError: function () {
            setActionLoading(null);
            setErrorMessage("Failed to update team status. Please try again.");
        },
    });
    var removeMutation = useRemoveTeamFromLeague({
        leagueId: id,
        onMutate: function (_a) {
            var teamId = _a.teamId;
            return setActionLoading(teamId);
        },
        onSuccess: function () {
            setActionLoading(null);
            setTeamToRemove(null);
            setSuccessMessage("Team has been removed from the league successfully!");
        },
        onError: function () {
            setActionLoading(null);
            setErrorMessage("Failed to remove team. Please try again.");
        },
    });
    var handleToggleStatus = function (teamId, currentStatus) {
        var team = teams === null || teams === void 0 ? void 0 : teams.find(function (t) { return t.team.id === teamId; });
        if (!team)
            return;
        if (currentStatus) {
            // Deactivating - show confirmation
            setTeamToDeactivate({
                id: teamId,
                name: team.team.name,
                isActive: currentStatus,
            });
        }
        else {
            // Activating - no confirmation needed
            updateStatusMutation.mutate({
                teamId: teamId,
                status: true,
            });
        }
    };
    var handleConfirmStatusChange = function () {
        if (teamToDeactivate) {
            updateStatusMutation.mutate({
                teamId: teamToDeactivate.id,
                status: !teamToDeactivate.isActive,
            });
            setTeamToDeactivate(null);
        }
    };
    var handleRemoveTeam = function () {
        if (teamToRemove) {
            removeMutation.mutate({ teamId: teamToRemove });
        }
    };
    var teamToRemoveData = teams === null || teams === void 0 ? void 0 : teams.find(function (t) { return t.team.id === teamToRemove; });
    if (!id) {
        return _jsx(NotFound, {});
    }
    if (isLoading || isPermsLoading || isTeamsLoading) {
        return _jsx(Loading, {});
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (error || !league || permsError || teamsError) {
        return (_jsx(ErrorView, { error: error ||
                permsError ||
                teamsError ||
                new Error("Failed to load league.") }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: updateStatusMutation.isError, message: "Failed to update team status. Please try again.", onClose: function () { } }), _jsx(ErrorSnackbar, { open: removeMutation.isError, message: "Failed to remove team. Please try again.", onClose: function () { } }), _jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "h4", sx: { mb: 1 }, children: "Manage Teams" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 2 }, children: ["Manage team memberships for ", league.name, ". You can temporarily deactivate teams or permanently remove them from the league."] }), _jsx(Button, { component: Link, to: "/leagues/".concat(id, "/"), variant: "outlined", size: "small", children: "\u2190 Back to League" })] }), _jsx(Card, { children: _jsxs(CardContent, { children: [_jsxs(Box, { sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 2,
                            }, children: [_jsxs(Typography, { variant: "h6", children: ["Teams (", (teams === null || teams === void 0 ? void 0 : teams.length) || 0, ")"] }), _jsx(Button, { component: Link, to: "/leagues/".concat(id, "/teams/add/"), variant: "contained", size: "small", children: "Add Teams" })] }), _jsx(Divider, { sx: { mb: 2 } }), teams && teams.length > 0 ? (_jsxs(React.Fragment, { children: [_jsx(Alert, { severity: "info", sx: { mb: 2 }, children: _jsxs(Typography, { variant: "body2", children: [_jsx("strong", { children: "Deactivate:" }), " Temporarily hide a team from the league (can be reactivated later)", _jsx("br", {}), _jsx("strong", { children: "Remove:" }), " Permanently delete the team's membership from this league"] }) }), _jsx(List, { children: teams
                                        .sort(function (a, b) {
                                        // First sort by active status (active first)
                                        if (a.isActive !== b.isActive) {
                                            return b.isActive ? 1 : -1;
                                        }
                                        // Then sort by name
                                        return a.team.name.localeCompare(b.team.name);
                                    })
                                        .map(function (membership, index) { return (_jsxs(ListItem, { sx: {
                                            borderBottom: index < teams.length - 1
                                                ? "1px solid rgba(0,0,0,0.08)"
                                                : "none",
                                            py: 2,
                                        }, children: [_jsx(ListItemAvatar, { sx: { mr: 3 }, children: _jsx(TeamAvatar, { team: membership.team }) }), _jsx(ListItemText, { primary: _jsxs(Box, { sx: {
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }, children: [_jsx(Typography, { variant: "body1", sx: { fontWeight: 600 }, children: membership.team.name }), _jsx(Chip, { label: membership.isActive ? "Active" : "Inactive", color: membership.isActive ? "success" : "default", size: "small" })] }), secondary: _jsxs(Stack, { spacing: 0.5, children: [membership.team.nickname && (_jsx(Typography, { variant: "caption", sx: { fontStyle: "italic" }, children: membership.team.nickname })), _jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Joined", " ", format(new Date(membership.dateJoined), "yyyy")] })] }) }), _jsxs(Box, { sx: { display: "flex", gap: 1 }, children: [_jsx(IconButton, { onClick: function () {
                                                            return handleToggleStatus(membership.team.id, membership.isActive);
                                                        }, disabled: actionLoading === membership.team.id, color: membership.isActive ? "warning" : "success", title: membership.isActive
                                                            ? "Deactivate team"
                                                            : "Activate team", children: membership.isActive ? (_jsx(DeactivateIcon, {})) : (_jsx(ActivateIcon, {})) }), _jsx(IconButton, { onClick: function () { return setTeamToRemove(membership.team.id); }, disabled: actionLoading === membership.team.id, color: "error", title: "Remove team from league", children: _jsx(DeleteIcon, {}) })] })] }, membership.id)); }) })] })) : (_jsxs(Box, { sx: { textAlign: "center", py: 4 }, children: [_jsx(Typography, { variant: "h6", color: "text.secondary", sx: { mb: 1 }, children: "No teams in this league" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 2 }, children: "Add some teams to get started" }), _jsx(Button, { component: Link, to: "/leagues/".concat(id, "/teams/add/"), variant: "contained", children: "Add Teams" })] }))] }) }), _jsx(DeleteDialog, { open: !!teamToRemove, title: "Remove ".concat(teamToRemoveData === null || teamToRemoveData === void 0 ? void 0 : teamToRemoveData.team.name, "?"), description: "\u26A0\uFE0F WARNING: This will permanently remove the team from this league and DELETE ALL associated matches, stats, and historical data for this team in this league. This action cannot be undone and may significantly impact your league's data.", onCancel: function () { return setTeamToRemove(null); }, onConfirm: handleRemoveTeam }), _jsxs(Dialog, { open: !!teamToDeactivate, onClose: function () { return setTeamToDeactivate(null); }, "aria-labelledby": "deactivate-dialog-title", "aria-describedby": "deactivate-dialog-description", children: [_jsx(DialogTitle, { id: "deactivate-dialog-title", children: _jsxs(Typography, { variant: "h6", color: "warning.main", children: ["Deactivate ", teamToDeactivate === null || teamToDeactivate === void 0 ? void 0 : teamToDeactivate.name, "?"] }) }), _jsx(DialogContent, { children: _jsx(DialogContentText, { id: "deactivate-dialog-description", children: "This will make the team inactive in this league. The team will no longer participate in new activities, but all existing data will be preserved. You can reactivate the team at any time." }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: function () { return setTeamToDeactivate(null); }, variant: "outlined", children: "Cancel" }), _jsx(Button, { onClick: handleConfirmStatusChange, variant: "contained", color: "warning", sx: { ml: 1 }, children: "Deactivate" })] })] }), _jsx(SuccessSnackbar, { open: !!successMessage, message: successMessage, onClose: function () { return setSuccessMessage(""); } }), _jsx(ErrorSnackbar, { open: !!errorMessage, message: errorMessage, onClose: function () { return setErrorMessage(""); } })] }));
};
